import { useState, useMemo, useEffect } from "react";
import {
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Radio,
  Stack,
  Box,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import BasicDateTimePicker from "./DateTimePicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import PageHeader from "./PageHeader";
import { citiesData } from "../../data/cities-data";

// 启用 UTC 和时区插件
dayjs.extend(utc);
dayjs.extend(timezone);
// 固定使用北京时间
dayjs.tz.setDefault("Asia/Shanghai");

function isDefaultDistrict(district) {
  if (!district) return true;
  return district.text === "--" || district.text.includes("时间") || district.text.includes("未知");
}

function PersonInfoInput({ onPaiPan, defaultValues, submitLabel = "立即排盘", hideHeader = false }) {
  const [userName, setUserName] = useState(defaultValues?.name || "");
  const [gender, setGender] = useState(defaultValues?.gender || "");
  const [birthday, setBirthday] = useState(
    defaultValues?.birthday_str ? dayjs(defaultValues.birthday_str).tz() : dayjs.tz()
  );
  const [useZaozi, setUseZaozi] = useState(defaultValues?.use_zaozi || false);
  const [useTrueSolar, setUseTrueSolar] = useState(defaultValues?.use_true_solar || false);
  const [longitude, setLongitude] = useState(defaultValues?.longitude ?? 120.0);
  const [birthplace, setBirthplace] = useState(defaultValues?.birthplace || "北京时间");

  const [provinceId, setProvinceId] = useState("");
  const [cityId, setCityId] = useState("");
  const [districtId, setDistrictId] = useState("");

  const [isErrorName, setIsErrorName] = useState(false);
  const [isErrorGender, setIsErrorGender] = useState(false);

  const provinces = citiesData;

  const selectedProvince = useMemo(() => {
    return provinces.find((p) => p.id === provinceId) || null;
  }, [provinceId, provinces]);

  const cities = useMemo(() => selectedProvince?.children || [], [selectedProvince]);

  const selectedCity = useMemo(() => {
    return cities.find((c) => c.id === cityId) || null;
  }, [cityId, cities]);

  const districts = useMemo(() => selectedCity?.children || [], [selectedCity]);

  const selectedDistrict = useMemo(() => {
    return districts.find((d) => d.id === districtId) || null;
  }, [districtId, districts]);

  // 根据选中地区更新经度和出生地描述
  useEffect(() => {
    if (!selectedDistrict) {
      setLongitude(120.0);
      setBirthplace("北京时间");
      return;
    }
    const placeName = `${selectedProvince?.text || ""} ${selectedCity?.text || ""} ${selectedDistrict?.text || ""}`.trim();
    setBirthplace(placeName);
    if (isDefaultDistrict(selectedDistrict)) {
      setLongitude(120.0);
    } else {
      setLongitude(selectedDistrict.gisGcj02Lng ?? 120.0);
    }
  }, [selectedProvince, selectedCity, selectedDistrict]);

  // 根据选中地区自动开关真太阳时
  useEffect(() => {
    if (!selectedDistrict || isDefaultDistrict(selectedDistrict)) {
      setUseTrueSolar(false);
    } else {
      setUseTrueSolar(true);
    }
  }, [selectedDistrict]);

  // 初始化级联选择（如果有 defaultValues）
  useEffect(() => {
    if (defaultValues?.birthplace && defaultValues.birthplace !== "北京时间") {
      const parts = defaultValues.birthplace.split(" ");
      if (parts.length >= 3) {
        const [pText, cText, dText] = parts;
        const p = provinces.find((prov) => prov.text === pText);
        if (p) {
          setProvinceId(p.id);
          const c = p.children?.find((city) => city.text === cText);
          if (c) {
            setCityId(c.id);
            const d = c.children?.find((dist) => dist.text === dText);
            if (d) {
              setDistrictId(d.id);
            }
          }
        }
      }
    } else {
      // 默认选中第一项（未知地 / 北京时间）
      const firstProvince = provinces[0];
      if (firstProvince) {
        setProvinceId(firstProvince.id);
        const firstCity = firstProvince.children?.[0];
        if (firstCity) {
          setCityId(firstCity.id);
          const firstDistrict = firstCity.children?.[0];
          if (firstDistrict) {
            setDistrictId(firstDistrict.id);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues?.birthplace]);

  function handleProvinceChange(e) {
    const pid = e.target.value;
    setProvinceId(pid);
    const p = provinces.find((prov) => prov.id === pid);
    const c = p?.children?.[0];
    setCityId(c?.id || "");
    const d = c?.children?.[0];
    setDistrictId(d?.id || "");
  }

  function handleCityChange(e) {
    const cid = e.target.value;
    setCityId(cid);
    const c = cities.find((city) => city.id === cid);
    const d = c?.children?.[0];
    setDistrictId(d?.id || "");
  }

  function handlePaiPan() {
    setIsErrorName(Boolean(!userName));
    setIsErrorGender(Boolean(!gender));
    if (!userName || !gender) {
      return;
    }

    const personInfo = {
      name: userName,
      gender: gender,
      birthday_str: birthday.utc().toISOString(),
      use_zaozi: useZaozi,
      use_true_solar: useTrueSolar,
      longitude: useTrueSolar ? Number(longitude) : undefined,
      birthplace: birthplace,
    };
    console.log(personInfo);
    onPaiPan(personInfo);
  }

  return (
    <Box sx={{ width: "100%" }}>
      {!hideHeader && <PageHeader title="凡心八字排盘" />}
      <Stack
        direction={"column"}
        spacing={2}
        alignItems={"stretch"}
        justifyContent={"flex-start"}
        sx={{ width: "100%", px: 2, pt: 2 }}
      >
        <TextField
          value={userName}
          error={isErrorName}
          id="standard-basic"
          label="姓名"
          variant="standard"
          helperText={isErrorName ? "请输入姓名" : ""}
          onChange={(e) => {
            const value = e.target.value;
            setUserName(value);
            setIsErrorName(!Boolean(value));
          }}
        />

        <RadioGroup
          value={gender}
          onChange={(e) => {
            const gender = e.target.value;
            console.log(gender);
            setGender(gender);
            setIsErrorGender(Boolean(!gender));
          }}
        >
          <Stack
            direction="row"
            alignItems={"center"}
            justifyContent={"flex-start"}
            spacing={2}
          >
            <FormLabel error={isErrorGender}>性别</FormLabel>
            <FormControlLabel value="男" control={<Radio />} label="男" />
            <FormControlLabel value="女" control={<Radio />} label="女" />
          </Stack>
        </RadioGroup>

        <BasicDateTimePicker
          datetime={birthday}
          onChange={(v) => setBirthday(v)}
        />

        {/* 省市区选择 */}
        <Stack direction="row" spacing={1}>
          <FormControl fullWidth size="small">
            <InputLabel>省/直辖市</InputLabel>
            <Select value={provinceId} label="省/直辖市" onChange={handleProvinceChange}>
              {provinces.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>市/区</InputLabel>
            <Select value={cityId} label="市/区" onChange={handleCityChange}>
              {cities.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>区/县</InputLabel>
            <Select
              value={districtId}
              label="区/县"
              onChange={(e) => setDistrictId(e.target.value)}
            >
              {districts.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <TextField
          label="出生地经度"
          type="number"
          size="small"
          fullWidth
          inputProps={{ step: 0.0001, min: 70, max: 140 }}
          value={longitude}
          onChange={(e) => {
            const val = e.target.value;
            if (val === "") {
              setLongitude("");
              setUseTrueSolar(false);
            } else {
              const num = Number(val);
              if (!Number.isNaN(num)) {
                setLongitude(num);
                if (num !== 120) {
                  setUseTrueSolar(true);
                } else {
                  setUseTrueSolar(false);
                }
              }
            }
          }}
          onBlur={(e) => {
            const val = e.target.value;
            if (val === "") {
              setLongitude(120.0);
              return;
            }
            const num = Number(val);
            if (Number.isNaN(num) || !Number.isFinite(num)) {
              setLongitude(120.0);
              return;
            }
            if (num < -180) setLongitude(-180);
            else if (num > 180) setLongitude(180);
            else setLongitude(parseFloat(num.toFixed(4)));
          }}
          helperText="东经为正，西经为负，范围 -180° ~ 180°。北京时间对应 120°"
        />

        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={useTrueSolar}
                onChange={(e) => setUseTrueSolar(e.target.checked)}
              />
            }
            label="真太阳时"
          />
          <FormControlLabel
            control={
              <Switch
                checked={useZaozi}
                onChange={(e) => setUseZaozi(e.target.checked)}
              />
            }
            label="早晚子时"
          />
        </Stack>

        <Button variant="contained" onClick={handlePaiPan}>
          {submitLabel}
        </Button>
      </Stack>
    </Box>
  );
}

function AddBaZiPage({ onPaiPan, defaultValues }) {
  return (
    <Box sx={{ width: "100%" }}>
      <PersonInfoInput onPaiPan={onPaiPan} defaultValues={defaultValues} />
    </Box>
  );
}
export { PersonInfoInput, AddBaZiPage };
